/**
 * API Service Module
 *
 * This module provides a centralised service for making API requests.
 * It handles common request patterns, error handling, and URL formatting.
 * Uses the singleton pattern to ensure only one instance exists.
 */

import { ApiError } from './apiError';

type ApiRetryOptions = {
  /** Additional attempts beyond the first. */
  attempts: number;
  /** Base delay in ms before the first retry. */
  delayMs: number;
  /** Multiplier applied to the delay per subsequent retry. */
  backoffFactor: number;
  /** Retryable check, given the response status and error. */
  retryOn: (status: number, error: Error | null) => boolean;
};

const DEFAULT_RETRY_OPTIONS: ApiRetryOptions = {
  attempts: 0,
  delayMs: 300,
  backoffFactor: 1.5,
  retryOn: status => !status || status === 408 || status === 429 || status >= 500,
};

type ApiRequestOptions = {
  params?: Record<string, string | number | boolean | string[] | number[]>;
  credentials?: RequestCredentials;
  next?: {
    revalidate?: number | false;
    tags?: string[];
  };
  retry?: Partial<ApiRetryOptions>;
} & RequestInit;

type ApiResponse<T> = {
  data: T | null;
  error: Error | null;
  status?: number;
  statusText?: string;
  headers?: Headers;
};

const HTTP_METHODS = {
  GET: 'GET',
  POST: 'POST',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
  HEAD: 'HEAD',
  OPTIONS: 'OPTIONS',
  TRACE: 'TRACE',
  CONNECT: 'CONNECT',
} as const;

type HttpMethod = (typeof HTTP_METHODS)[keyof typeof HTTP_METHODS];

const METHODS_WITH_BODY = [HTTP_METHODS.POST, HTTP_METHODS.PUT, HTTP_METHODS.PATCH] as const;
type MethodsWithBody = (typeof HTTP_METHODS)[keyof Pick<typeof HTTP_METHODS, 'POST' | 'PUT' | 'PATCH'>];

/**
 * Service class for making API requests
 * Provides methods for common HTTP verbs (GET, POST, PUT, DELETE)
 * Implements singleton pattern to ensure only one instance exists
 */
class ApiService {
  private static instance: ApiService | null = null;

  private constructor(
    private readonly baseUrl: string,
    private readonly defaultCredentials: RequestCredentials = 'same-origin'
  ) {}

  /**
   * Gets the singleton instance of ApiService
   * @param baseUrl - The base URL for API requests (required for first initialisation)
   * @param defaultCredentials - Default credentials policy for requests
   * @returns The singleton instance of ApiService
   */
  public static getInstance(baseUrl?: string, defaultCredentials?: RequestCredentials): ApiService {
    if (ApiService.instance) {
      return ApiService.instance;
    }

    if (!baseUrl) {
      throw new Error('Base URL is required for initialising ApiService');
    }

    ApiService.instance = new ApiService(baseUrl, defaultCredentials);
    return ApiService.instance;
  }

  /**
   * Resets the singleton instance (useful for testing or reconfiguration)
   * @param baseUrl - The new base URL for API requests
   * @param defaultCredentials - New default credentials policy
   * @returns The new singleton instance of ApiService
   */
  public static resetInstance(baseUrl: string, defaultCredentials?: RequestCredentials): ApiService {
    ApiService.instance = new ApiService(baseUrl, defaultCredentials);
    return ApiService.instance;
  }

  /**
   * Normalizes headers from HeadersInit to Record<string, string>
   */
  private normalizeHeaders(headers?: HeadersInit): Record<string, string> {
    if (!headers) return {};

    // Handle Headers object case first
    if (typeof Headers !== 'undefined' && headers instanceof Headers) {
      const normalized: Record<string, string> = {};
      headers.forEach((value, key) => {
        normalized[key] = value;
      });
      return normalized;
    }

    // Handle string[][] case
    if (Array.isArray(headers)) {
      const normalized: Record<string, string> = {};
      for (const [key, value] of headers) {
        normalized[key] = value;
      }
      return normalized;
    }

    // Handle Record<string, string> case - use type assertion for safety
    if (typeof headers === 'object' && !Array.isArray(headers)) {
      return { ...(headers as Record<string, string>) };
    }

    return {};
  }

  /**
   * Performs a GET request to the specified path
   * @param path - The API endpoint path
   * @param options - Request options including query parameters
   * @returns Promise resolving to ApiResponse with data or error
   */
  async get<T = unknown>(path: string, options?: ApiRequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>(path, HTTP_METHODS.GET, undefined, options);
  }

  /**
   * Performs a POST request to the specified path
   * @param path - The API endpoint path
   * @param data - Optional data to be sent in the request body
   * @param options - Additional request options
   * @returns Promise resolving to ApiResponse with data or error
   */
  async post<T = unknown>(path: string, data?: unknown, options?: ApiRequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>(path, HTTP_METHODS.POST, data, options);
  }

  /**
   * Performs a PUT request to the specified path
   * @param path - The API endpoint path
   * @param data - Optional data to be sent in the request body
   * @param options - Additional request options
   * @returns Promise resolving to ApiResponse with data or error
   */
  async put<T = unknown>(path: string, data?: unknown, options?: ApiRequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>(path, HTTP_METHODS.PUT, data, options);
  }

  /**
   * Performs a PATCH request to the specified path
   * @param path - The API endpoint path
   * @param data - Optional data to be sent in the request body as JSON
   * @param options - Additional request options
   * @returns Promise resolving to ApiResponse with data or error
   */
  async patch<T = unknown>(path: string, data?: unknown, options?: ApiRequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>(path, HTTP_METHODS.PATCH, data, options);
  }

  /**
   * Performs a DELETE request to the specified path
   * @param path - The API endpoint path
   * @param options - Request options
   * @returns Promise resolving to ApiResponse with data or error
   */
  async delete<T = unknown>(path: string, options?: ApiRequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>(path, HTTP_METHODS.DELETE, undefined, options);
  }

  /**
   * Generic request method to handle all HTTP methods
   * @param path - The API endpoint path
   * @param method - The HTTP method to use
   * @param data - Optional data to be sent in the request body
   * @param options - Additional request options
   * @returns Promise resolving to ApiResponse with data or error
   * @private
   */
  private async request<T = unknown>(path: string, method: HttpMethod, data?: unknown, options?: ApiRequestOptions): Promise<ApiResponse<T>> {
    const hasBody = METHODS_WITH_BODY.includes(method as MethodsWithBody);

    // Determine body and headers based on explicit Content-Type
    const explicitContentType = options?.headers && this.normalizeHeaders(options.headers)['Content-Type'];

    const requestBody: BodyInit | undefined = this.getRequestBody(hasBody, data, explicitContentType);

    return this.fetcher<T>(path, {
      ...options,
      method,
      body: requestBody,
      headers: {
        // Only set default JSON content-type if no explicit header and there is a body, but not for FormData
        ...(hasBody && requestBody && !explicitContentType && !(requestBody instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
        ...options?.headers,
      },
    });
  }

  /**
   * Gets the request body based on the explicit content type
   * @param hasBody - Whether the request has a body
   * @param data - The data to be sent in the request body
   * @param explicitContentType - The explicit content type of the request
   * @returns The request body
   */
  private getRequestBody(hasBody: boolean, data: unknown, explicitContentType: string | undefined): BodyInit | undefined {
    if (!hasBody || !data) {
      return undefined;
    }

    // Handle FormData directly
    if (data instanceof FormData) {
      return data;
    }

    switch (explicitContentType) {
      case 'application/x-www-form-urlencoded': {
        const params = new URLSearchParams();
        const source = data as Record<string, string | number | boolean | null | undefined>;
        Object.entries(source).forEach(([key, value]) => {
          if (value !== undefined && value !== null) params.set(key, String(value));
        });
        return params.toString();
      }
      case 'application/json':
        return JSON.stringify(data);
      default:
        // Default to JSON serialisation when no explicit content type is provided
        return typeof data === 'string' ? (data as BodyInit) : JSON.stringify(data);
    }
  }

  /**
   * Core method for making HTTP requests
   * @param path - The API endpoint path
   * @param options - Request options including method, headers, and body
   * @returns Promise resolving to ApiResponse with data or error
   * @private
   */
  private async fetcher<T = unknown>(path: string, options: ApiRequestOptions = {}): Promise<ApiResponse<T>> {
    const url = this.buildUrl(path, options.params);
    const { attempts, delayMs, backoffFactor, retryOn } = { ...DEFAULT_RETRY_OPTIONS, ...options.retry };

    let response: ApiResponse<T>;
    for (let attempt = 0; attempt <= attempts; attempt++) {
      response = await this.performFetch<T>(url, options);

      if (!response.error) {
        return response;
      }

      const isRetryable = retryOn(response.status ?? 0, response.error);

      if (!isRetryable || attempt === attempts) {
        return response;
      }

      await new Promise(resolve => setTimeout(resolve, delayMs * Math.pow(backoffFactor, attempt)));
    }

    return response!;
  }

  /**
   * Performs a single fetch attempt against the given URL
   * @param url - The fully-built request URL
   * @param options - Request options including method, headers, and body
   * @returns Promise resolving to ApiResponse with data or error
   * @private
   */
  private async performFetch<T = unknown>(url: string, options: ApiRequestOptions): Promise<ApiResponse<T>> {
    const normalizedHeaders = this.normalizeHeaders(options.headers);

    try {
      const response = await fetch(url, {
        ...options,
        credentials: options.credentials || this.defaultCredentials,
        headers: normalizedHeaders,
      });

      if (!response.ok) {
        // Try to read the error response body for detailed error information
        try {
          const errorData = await response.json();
          const enhancedError = new ApiError(errorData.message || `API error: ${response.status} ${response.statusText}`, response.status, response.statusText, url, response);

          // Attach the error data to the error object so it can be accessed
          (enhancedError as ApiError & { responseData: unknown }).responseData = errorData;

          return {
            data: null,
            error: enhancedError,
            status: response.status,
            statusText: response.statusText,
          };
        } catch {
          // If we can't parse the error response, fall back to the regular error
          return this.createErrorResponse<T>(response, url);
        }
      }

      // Handle empty response body (e.g., 200 OK with no content)
      const contentType = response.headers.get('content-type');
      const hasContent = response.headers.get('content-length') !== '0' && contentType && contentType.includes('application/json');

      if (hasContent) {
        try {
          return {
            data: await response.json(),
            error: null,
            status: response.status,
            statusText: response.statusText,
            headers: response.headers,
          };
        } catch (jsonError) {
          // Handle JSON parsing errors
          const error = jsonError instanceof Error ? jsonError : new Error(String(jsonError));
          return this.createErrorResponse<T>(response, url, error);
        }
      } else {
        // Return null for empty responses (like 200 OK with no body)
        return {
          data: null,
          error: null,
          status: response.status,
          statusText: response.statusText,
          headers: response.headers,
        };
      }
    } catch (error) {
      const networkError = error instanceof Error ? error : new Error(String(error));
      return this.createErrorResponse<T>(
        {
          status: 0,
          statusText: 'Network Error',
        } as Response,
        url,
        networkError
      );
    }
  }

  /**
   * Builds the complete URL with query parameters
   * @param path - The API endpoint path
   * @param params - Optional query parameters to append to the URL
   * @returns The complete URL with query parameters
   * @private
   */
  private buildUrl(path: string, params?: Record<string, string | number | boolean | string[] | number[]>): string {
    let url = `${this.baseUrl}${path}`;

    if (params && Object.keys(params).length > 0) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          // Handle array parameters by adding multiple entries with the same key
          value.forEach(item => searchParams.append(key, String(item)));
        } else {
          searchParams.append(key, String(value));
        }
      });
      url = `${url}${url.includes('?') ? '&' : '?'}${searchParams.toString()}`;
    }

    return url;
  }

  /**
   * Creates a standardised error response
   * @param response - The Response object from fetch
   * @param url - The URL that was requested
   * @param error - Optional custom error to use instead of creating a new ApiError
   * @returns ApiResponse with error details
   * @private
   */
  private createErrorResponse<T>(response: Response, url: string, error?: Error): ApiResponse<T> {
    const apiError = error || new ApiError(`API error: ${response.status} ${response.statusText}`, response.status, response.statusText, url, response);

    return {
      data: null,
      error: apiError,
      status: response.status,
      statusText: response.statusText,
    };
  }
}

export { ApiService, HTTP_METHODS, METHODS_WITH_BODY };
export type { ApiRequestOptions, ApiResponse, ApiRetryOptions, MethodsWithBody };
